import React, { useState, useEffect, useMemo, lazy, Suspense } from "react";
import "./Home.css";
import { conteoParams } from "../../interpreter/role";
import { monthTechnician } from "../../interpreter/technician";
import { Headers } from "../../layouts/headers/headers";
import { MainContent } from "../../layouts/MainContent";
import { HeadersCentral } from "../../layouts/headers-central/headers-central";
import { useAuth } from "../../contexts/authContexts";
import { monthTranslations } from "../../services/diccionaryMonth";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

// Íconos livianos de react-icons
import { MdArrowForward, MdGroup, MdConstruction, MdInbox, MdAssignmentTurnedIn } from "react-icons/md";

// Lazy modales
const UserModal = lazy(() => import("../../components/modals/profileUser"));
const DetailTechnician = lazy(() => import("../../components/modals/detailsContent").then(m => ({ default: m.DetailTechnician })));
const DetailClient = lazy(() => import("../../components/modals/detailsContent").then(m => ({ default: m.DetailClient })));
const DetailRequest = lazy(() => import("../../components/modals/detailsContent").then(m => ({ default: m.DetailRequest })));
const DetailService = lazy(() => import("../../components/modals/detailsContent").then(m => ({ default: m.DetailService })));

ChartJS.register(ArcElement, Tooltip, Legend);

const Home = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conteo, setConteo] = useState({ clientesInternos: 0, tecnicos: 0, solicitudes: 0, servicios: 0 });
  const [technicianStats, setTechnicianStats] = useState({ months: [], counts: [] });

  const { user } = useAuth();
  const tieneRol = user?.role?.some(role => role.name === "Administrativo");
  const tienePermiso = user?.permissions?.some(permiso => permiso.name === "ver_dashboard");

  useEffect(() => {
    if (!tieneRol && !tienePermiso) {
      alert("⚠️ Acceso denegado: El usuario no tiene los permisos adecuados.");
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await conteoParams();
        if (response.success) {
          setConteo({
            clientesInternos: response.cont.client || 0,
            tecnicos: response.cont.technician || 0,
            solicitudes: response.cont.requests || 0,
            servicios: response.cont.service || 0,
          });
        }

        const responseDatos = await monthTechnician();
        if (responseDatos.status === 1) {
          const months = responseDatos.content.map(item => monthTranslations[item.month] || item.month);
          const counts = responseDatos.content.map(item => item.technicians_count);
          setTechnicianStats({ months, counts });
        }
      } catch (error) {
        console.error("❌ Error al obtener los datos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const openModal = (type) => setModalType(type);
  const closeModal = () => setModalType(null);

  const data = useMemo(() => ({
    labels: technicianStats.months,
    datasets: [
      {
        label: "Técnicos a vencer",
        data: technicianStats.counts,
        backgroundColor: ["#07b310", "#3069f0", "#c93afc"],
        borderColor: "#FFFFFF",
        borderWidth: 2
      },
    ],
  }), [technicianStats]);

  const dashboardItems = [
    { label: 'Clientes Internos', count: conteo.clientesInternos, icon: <MdGroup size={32} />, color: 'blue', modal: 'client' },
    { label: 'Técnicos', count: conteo.tecnicos, icon: <MdConstruction size={32} />, color: 'purple', modal: 'technician' },
    { label: 'Solicitudes', count: conteo.solicitudes, icon: <MdInbox size={32} />, color: 'blue', modal: 'request' },
    { label: 'Servicios', count: conteo.servicios, icon: <MdAssignmentTurnedIn size={32} />, color: 'yellow', modal: 'service' }
  ];

  return (
    <div className="flex h-screen">
      <Headers />
      <MainContent>
        <HeadersCentral showDropdown={showDropdown} setShowDropdown={setShowDropdown} openModal={() => openModal('user')} />

        <main className="p-6">
          <div className="flex items-center gap-3 mb-7">
            <h1 className="text-3xl font-bold text-black">Dashboard</h1>
            <p className="text-gray-600 text-lg italic">¡Bienvenido al dashboard de <span className="font-bold text-gray-800">Muv Service</span>!</p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-500 border-solid"></div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {dashboardItems.map((item, idx) => (
                  <div key={idx} className={`bg-${item.color}-500 text-white p-4 rounded-lg shadow-md`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="mb-2">{item.icon}</div>
                        <h2 className="text-lg font-semibold">{item.label}</h2>
                        <p className="text-3xl font-bold">{item.count}</p>
                      </div>
                      <button
                        onClick={() => openModal(item.modal)}
                        className={`bg-white text-${item.color}-500 rounded-full p-2 shadow-md hover:bg-${item.color}-100`}
                      >
                        <MdArrowForward size={20} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-white p-6 rounded-3xl shadow-2xl w-full md:w-1/2 md:ml-1">
                <h2 className="text-3xl font-bold mb-4 text-center">Estadísticas de Técnicos por Mes</h2>
                <div className="flex justify-center items-center h-full">
                  <div className="w-[450px] h-[450px] md:w-[500px] md:h-[500px]">
                    <Doughnut
                      key={technicianStats.months.join("-") || "chart-empty"}
                      data={data}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'top',
                            labels: {
                              boxWidth: 15,
                              padding: 10,
                              font: { size: 16 },
                            },
                          },
                        },
                        cutout: '60%',
                      }}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </main>
      </MainContent>
      <Suspense fallback={null}>
        {modalType === 'user' && <UserModal isOpen onClose={closeModal} user={user} />}
        {modalType === 'technician' && <DetailTechnician isOpen onClose={closeModal} />}
        {modalType === 'client' && <DetailClient isOpen onClose={closeModal} />}
        {modalType === 'request' && <DetailRequest isOpen onClose={closeModal} />}
        {modalType === 'service' && <DetailService isOpen onClose={closeModal} technicianData={0} />}
      </Suspense>
    </div>
  );
};

export default Home;
